import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Empty, Form, Input, message, Modal, Pagination, Space, Spin, Table } from "antd";
import { Api, IChatLessonCourseTemplate } from "@model";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIsCompact } from "../../../hooks/useIsCompact";
import {
  COURSE_TEMPLATE_PLACEHOLDERS_HINT_STYLE,
  COURSE_TEMPLATE_PLACEHOLDERS_TEXT,
} from "../../ChatLesson/courseTemplatePlaceholders";

export default function CourseTemplatesSettings() {
  const isCompact = useIsCompact();
  const isMounted = useRef(true);
  const [list, setList] = useState<IChatLessonCourseTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  type CourseTemplateForm = {
    name: string;
    template: string;
  };

  const [form] = Form.useForm<CourseTemplateForm>();
  const [page, setPage] = useState(1);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchList = useCallback(() => {
    setLoading(true);
    Api.get("/api/admin/panel/chat-lesson-course-templates", {
      params: { limit: 500 },
    })
      .then((res) => {
        if (!isMounted.current) return;
        const raw = res?.data?.data;
        setList(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (isMounted.current) {
          message.error("Failed to load templates");
          setList([]);
        }
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row: IChatLessonCourseTemplate) => {
    setEditingId(row._id);
    form.setFieldsValue({
      name: row.name,
      template: row.template,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    form.resetFields();
  };

  const submitModal = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingId) {
        await Api.put(
          `/api/admin/panel/chat-lesson-course-templates/${editingId}`,
          values,
        );
        if (isMounted.current) message.success("Template updated");
      } else {
        await Api.post("/api/admin/panel/chat-lesson-course-templates", values);
        if (isMounted.current) message.success("Template created");
      }
      closeModal();
      fetchList();
    } catch (e: unknown) {
      if (isMounted.current && (e as { errorFields?: unknown })?.errorFields)
        return;
      if (isMounted.current) message.error("Save failed");
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const confirmDelete = (row: IChatLessonCourseTemplate) => {
    Modal.confirm({
      title: "Delete template",
      content: `Remove “${row.name}”? Chat lessons referencing it may need updating.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await Api.delete(
            `/api/admin/panel/chat-lesson-course-templates/${row._id}`,
          );
          if (isMounted.current) {
            message.success("Template deleted");
            fetchList();
          }
        } catch {
          if (isMounted.current) message.error("Delete failed");
        }
      },
    });
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          htmlType="button"
          block={isCompact}
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          New template
        </Button>
      </div>
      {isCompact ? (
        <Spin spinning={loading}>
          {list.length === 0 && !loading ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No templates" />
          ) : (
            list.slice((page - 1) * 20, page * 20).map((row) => (
              <div
                key={row._id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{row.name}</div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 6, wordBreak: "break-word" }}>
                  {row.template?.slice(0, 160) ?? ""}
                  {row.template && row.template.length > 160 ? "…" : ""}
                </div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                  {row.createDate ? new Date(row.createDate).toLocaleString() : "—"}
                </div>
                <Space size={0} wrap>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
                    Edit
                  </Button>
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => confirmDelete(row)}
                  >
                    Delete
                  </Button>
                </Space>
              </div>
            ))
          )}
          {list.length > 20 && (
            <Pagination
              style={{ marginTop: 8, textAlign: "center" }}
              size="small"
              simple
              current={page}
              pageSize={20}
              total={list.length}
              onChange={setPage}
            />
          )}
        </Spin>
      ) : (
      <Table<IChatLessonCourseTemplate>
        rowKey="_id"
        loading={loading}
        dataSource={list}
        size="small"
        pagination={{ pageSize: 20 }}
        columns={[
          { title: "Name", dataIndex: "name", key: "name", width: 200 },
          {
            title: "Template preview",
            dataIndex: "template",
            key: "template",
            ellipsis: true,
            width: "40%",
            render: (t: string) => (
              <span>
                {t?.slice(0, 120) ?? ""}
                {t && t.length > 120 ? "…" : ""}
              </span>
            ),
          },
          {
            title: "Created",
            dataIndex: "createDate",
            key: "createDate",
            width: 180,
            render: (d: string) => (d ? new Date(d).toLocaleString() : "—"),
          },
          {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: unknown, row) => (
              <Space>
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(row)}
                >
                  Edit
                </Button>
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => confirmDelete(row)}
                >
                  Delete
                </Button>
              </Space>
            ),
          },
        ]}
      />
      )}

      <Modal
        title={editingId ? "Edit course template" : "New course template"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={submitModal}
        confirmLoading={saving}
        width={isCompact ? "100%" : "90vw"}
        destroyOnClose
        maskClosable={false}
        okText="Save"
        style={{ height: "85vh", overflowY: "auto" }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Enter a name" }]}
          >
            <Input placeholder="e.g. Café roleplay" />
          </Form.Item>
          <Form.Item
            name="template"
            label={
              <div>
                <div>Template body</div>
                <div style={COURSE_TEMPLATE_PLACEHOLDERS_HINT_STYLE}>
                  Placeholders: {COURSE_TEMPLATE_PLACEHOLDERS_TEXT}
                </div>
              </div>
            }
            rules={[{ required: true, message: "Enter template text" }]}
          >
            <Input.TextArea
              style={{ resize: "vertical" }}
              placeholder="System prompt template…"
              rows={isCompact ? 12 : 35}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
